import React, { useState, useEffect } from 'react';
import { Plus, Calendar, User, AlertCircle, Clock, CheckCircle, PlayCircle } from 'lucide-react';
import { ProjectService } from '../../services/projectService';
import { CreateTaskModal } from './CreateTaskModal';
import { EditTaskModal } from './EditTaskModal';
import { Task } from '../../types';
import { GoogleDirectoryUser } from '../../services/googleDirectory';
import { LoadingSpinner } from '../shared';

interface TaskBoardProps {
  projectId: string;
  projectMembers?: GoogleDirectoryUser[];
  onTasksChanged?: () => void;
}

interface TaskCardProps {
  task: Task;
  projectMembers?: GoogleDirectoryUser[];
  onTaskUpdate?: (taskId: string, updates: Partial<Task>) => void;
  onTaskClick?: (task: Task) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, projectMembers, onTaskUpdate, onTaskClick }) => {
  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'low': return 'border-l-green-500 bg-green-50';
      case 'medium': return 'border-l-yellow-500 bg-yellow-50';
      case 'high': return 'border-l-red-500 bg-red-50';
      default: return 'border-l-gray-300 bg-white';
    }
  };

  const assignedUser = projectMembers?.find(member => member.id === task.assigneeId);

  const handleStatusChange = (newStatus: Task['status']) => {
    if (onTaskUpdate) {
      onTaskUpdate(task.id, { status: newStatus });
    }
  };

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';

  return (
    <div 
      className={`bg-white rounded-lg border-l-4 ${getPriorityColor(task.priority)} p-4 mb-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
      onClick={() => onTaskClick && onTaskClick(task)}
    >
      {/* Task Title */}
      <h4 className="font-medium text-gray-900 mb-2 line-clamp-2">{task.title}</h4>
      
      {/* Task Description */}
      {task.description && (
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{task.description}</p>
      )}

      {/* Task Meta Information */}
      <div className="space-y-2">
        {/* Due Date */}
        {task.dueDate && (
          <div className={`flex items-center text-xs ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
            <Calendar className="w-3 h-3 mr-1" />
            <span>{isOverdue ? 'Overdue: ' : 'Due: '}{new Date(task.dueDate).toLocaleDateString()}</span>
            {isOverdue && <AlertCircle className="w-3 h-3 ml-1" />}
          </div>
        )}

        {/* Assignee */}
        {assignedUser && (
          <div className="flex items-center text-xs text-gray-500">
            <User className="w-3 h-3 mr-1" />
            <span>{assignedUser.name.fullName}</span>
          </div>
        )}

        {/* Priority Badge */}
        <div className="flex items-center justify-between">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
            task.priority === 'high' ? 'bg-red-100 text-red-700' :
            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
            'bg-green-100 text-green-700'
          }`}>
            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
          </span>

          {/* Quick Status Actions */}
          <div className="flex space-x-1">
            {task.status !== 'todo' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusChange('todo');
                }}
                className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
                title="Move to To Do"
              >
                <Clock className="w-3 h-3" />
              </button>
            )}
            {task.status !== 'in_progress' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusChange('in_progress');
                }}
                className="p-1 hover:bg-blue-200 rounded text-gray-400 hover:text-blue-600"
                title="Move to In Progress"
              >
                <PlayCircle className="w-3 h-3" />
              </button>
            )}
            {task.status !== 'completed' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusChange('completed');
                }}
                className="p-1 hover:bg-green-200 rounded text-gray-400 hover:text-green-600"
                title="Mark as Complete"
              >
                <CheckCircle className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TaskBoard: React.FC<TaskBoardProps> = ({ projectId, projectMembers = [], onTasksChanged }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const projectService = ProjectService.getInstance();

  useEffect(() => {
    loadTasks();
  }, [projectId]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('📋 Loading project tasks...');
      const projectTasks = await projectService.getProjectTasks(projectId);
      
      console.log('✅ Loaded tasks:', projectTasks.length);
      setTasks(projectTasks);
    } catch (err: any) {
      console.error('❌ Error loading tasks:', err);
      setError(`Failed to load tasks: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskCreated = (newTask: Task) => {
    setTasks(prevTasks => [newTask, ...prevTasks]);
    console.log('Task created and added to board:', newTask);
    
    // Notify parent about task changes
    if (onTasksChanged) {
      onTasksChanged();
    }
  };

  const handleTaskUpdate = async (taskId: string, updates: Partial<Task>) => {
    try {
      console.log('🔄 Updating task:', taskId, updates);
      
      await projectService.updateTask(taskId, updates);
      
      // Update task in local state
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, ...updates, updatedAt: new Date() }
            : task
        )
      );
      
      console.log('✅ Task updated successfully');
      
      // Notify parent about task changes
      if (onTasksChanged) {
        onTasksChanged();
      }
    } catch (err: any) {
      console.error('❌ Error updating task:', err);
      setError(`Failed to update task: ${err.message || 'Unknown error'}`);
    }
  };

  const handleTaskClick = (task: Task) => {
    setEditingTask(task);
  };

  const handleTaskUpdated = (updatedTask: Task) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === updatedTask.id ? updatedTask : task
      )
    );
    setEditingTask(null);
    console.log('✅ Task updated from modal:', updatedTask);
    
    // Notify parent about task changes
    if (onTasksChanged) {
      onTasksChanged();
    }
  };

  const handleTaskDeleted = (deletedTaskId: string) => {
    setTasks(prevTasks =>
      prevTasks.filter(task => task.id !== deletedTaskId)
    );
    setEditingTask(null);
    console.log('✅ Task deleted:', deletedTaskId);
    
    // Notify parent about task changes
    if (onTasksChanged) {
      onTasksChanged();
    }
  };

  const getTasksByStatus = (status: Task['status']) => {
    return tasks.filter(task => task.status === status);
  };

  const getColumnTitle = (status: Task['status']) => {
    const taskCount = getTasksByStatus(status).length;
    switch (status) {
      case 'todo': return `To Do (${taskCount})`;
      case 'in_progress': return `In Progress (${taskCount})`;
      case 'completed': return `Completed (${taskCount})`;
      default: return `${status} (${taskCount})`;
    }
  };

  const getColumnColor = (status: Task['status']) => {
    switch (status) {
      case 'todo': return 'bg-gray-100 border-gray-300';
      case 'in_progress': return 'bg-blue-100 border-blue-300';
      case 'completed': return 'bg-green-100 border-green-300';
      default: return 'bg-gray-100 border-gray-300';
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading tasks..." size="md" fullPage />;
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Task Board</h3>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Task
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-full">
        {(['todo', 'in_progress', 'completed'] as const).map((status) => (
          <div key={status} className={`rounded-lg border-2 border-dashed ${getColumnColor(status)} p-4 min-h-96`}>
            {/* Column Header */}
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-700">{getColumnTitle(status)}</h4>
              <div className="flex items-center">
                {status === 'todo' && <Clock className="w-4 h-4 text-gray-500" />}
                {status === 'in_progress' && <PlayCircle className="w-4 h-4 text-blue-500" />}
                {status === 'completed' && <CheckCircle className="w-4 h-4 text-green-500" />}
              </div>
            </div>

            {/* Tasks */}
            <div className="space-y-3">
              {getTasksByStatus(status).map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  projectMembers={projectMembers}
                  onTaskUpdate={handleTaskUpdate}
                  onTaskClick={handleTaskClick}
                />
              ))}
              
              {/* Empty State */}
              {getTasksByStatus(status).length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-sm">
                    {status === 'todo' && 'No tasks to do'}
                    {status === 'in_progress' && 'No tasks in progress'}
                    {status === 'completed' && 'No completed tasks'}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onTaskCreated={handleTaskCreated}
        projectId={projectId}
        projectMembers={projectMembers}
      />

      {/* Edit Task Modal */}
      {editingTask && (
        <EditTaskModal
          isOpen={!!editingTask}
          onClose={() => setEditingTask(null)}
          onTaskUpdated={handleTaskUpdated}
          onTaskDeleted={handleTaskDeleted}
          task={editingTask}
          projectMembers={projectMembers}
        />
      )}
    </div>
  );
};
